// src/pages/DocketGenie/index.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import NewHeader from './components/NewHeader';
import EnhancedResultsSection from './components/EnhancedResultsSection';
import CaseDetailsModal from './components/CaseDetailsModal';
import ScraperManagementDashboard from './components/AdminDashboard';
import LegalDisclaimerModal from '../../components/LegalDisclaimerModal';
import EmailVerification from '../../components/EmailVerification';
import JudgeProsecutorAnalytics from './components/JudgeProsecutorAnalytics';
// import AttorneyPerformancePane from './components/AttorneyPerformancePane'; // Temporarily disabled
import { useFetch } from '../../hooks/useFetch';
import { API_URLS } from '../../constants/api';
import { useUsageTracking } from '../../hooks/useUsageTracking';
import UsageDisplay from '../../components/UsageDisplay';

import {
  saveRecentSearch,
  getRecentSearches
} from '../../utils/recentSearches';

const DocketGenie = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [searchBlocked, setSearchBlocked] = useState(false);
  const { data: courts } = useFetch(API_URLS.COURTS);

  // Usage tracking for free tier
  const { canPerformAction, incrementUsage, isInTrial } = useUsageTracking();

  // Wrapper for setSearchQuery that also resets searchExecuted
  const handleSearchQueryChange = useCallback(
    (newQuery) => {
      setSearchQuery(newQuery);
      // Reset search executed state when user types
      if (searchExecuted) {
        setSearchExecuted(false);
      }
    },
    [searchExecuted]
  );

  // Check if admin access is enabled based on environment
  // In development: require explicit enablement via REACT_APP_ADMIN_ENABLED=true
  // In production: require explicit enablement via REACT_APP_ADMIN_ENABLED=true
  const isAdminEnabled = process.env.REACT_APP_ADMIN_ENABLED === 'true';

  // Admin authentication handler (frontend-only)
  const handleAdminLogin = async (password) => {
    setIsAuthLoading(true);
    setAuthError('');

    try {
      // Simple frontend-only authentication
      // In production, you should use a more secure method
      const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';

      if (password === adminPassword) {
        setIsAuthenticated(true);
        setIsAdminMode(true);
      } else {
        setAuthError('Invalid password');
      }
    } catch (error) {
      setAuthError('Authentication error. Please try again.');
      console.error('Admin auth error:', error);
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Admin logout handler
  const handleAdminLogout = () => {
    setIsAuthenticated(false);
    setIsAdminMode(false);
    setAuthError('');
  };

  // Debug logging removed for performance
  // Initialize filters from URL params or defaults
  const getInitialFilters = () => {
    const urlFilters = searchParams.get('filters');
    if (urlFilters) {
      try {
        return JSON.parse(decodeURIComponent(urlFilters));
      } catch (e) {
        console.error('Error parsing filters from URL:', e);
      }
    }

    return {
      jurisdiction: '',
      courthouse: '',
      departments: [],
      caseStatus: '',
      judges: [],
      defendants: [],
      prosecutingAttorneys: [],
      defenseAttorneys: [],
      charges: [],
      chargeLogic: 'OR',
      sentencingTypes: [],
      demographics: {
        race: null,
        minAge: null,
        maxAge: null,
        gender: null
      },
      similarTo: null
    };
  };

  const [filters, setFiltersBase] = useState(getInitialFilters);

  // Direct setFilters without logging for performance
  // const setFilters = setFiltersBase; // Unused - using setFiltersWithURL instead

  const [pagination, setPagination] = useState({
    currentPage: parseInt(searchParams.get('page')) || 1,
    pageSize: 24,
    totalResults: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [executionTime, setExecutionTime] = useState(null);

  // New state for Phase 2 features
  const [showSearchInsights, setShowSearchInsights] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [sourceCase, setSourceCase] = useState(null);

  // Sorting state
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Load recent searches on component mount
  useEffect(() => {
    setRecentSearches(getRecentSearches());
  }, []);

  // Prevent background scrolling when mobile search insights modal is open
  useEffect(() => {
    if (showSearchInsights) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      const originalTop = window.getComputedStyle(document.body).top;
      const scrollY = window.scrollY;

      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      const preventTouchMove = (e) => {
        const modal = document.querySelector('[data-modal-content]');
        if (modal && !modal.contains(e.target)) {
          e.preventDefault();
        }
      };

      document.addEventListener('touchmove', preventTouchMove, {
        passive: false
      });
      document.addEventListener('touchstart', preventTouchMove, {
        passive: false
      });

      return () => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = originalStyle;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = '';
        document.removeEventListener('touchmove', preventTouchMove);
        document.removeEventListener('touchstart', preventTouchMove);
        window.scrollTo(0, scrollY);
      };
    }
  }, [showSearchInsights]);

  // Note: Similarity search is now handled via the similarTo filter

  // Update URL when search state changes
  const updateURL = useCallback(
    (newSearchQuery, newFilters, newPage = 1) => {
      const params = new URLSearchParams();
      if (newSearchQuery) params.set('q', newSearchQuery);
      if (newPage > 1) params.set('page', newPage.toString());

      // Only add filters to URL if there are active filters
      const hasActiveFilters =
        newFilters?.courthouse ||
        newFilters?.caseStatus ||
        newFilters?.judges?.length > 0 ||
        newFilters?.charges?.length > 0 ||
        newFilters?.defendants?.length > 0 ||
        newFilters?.prosecutingAttorneys?.length > 0 ||
        newFilters?.defenseAttorneys?.length > 0;

      if (hasActiveFilters) {
        params.set('filters', encodeURIComponent(JSON.stringify(newFilters)));
      }

      setSearchParams(params);
    },
    [setSearchParams]
  );

  // Wrapper for setFilters that also updates URL
  const setFiltersWithURL = (newFilters) => {
    setFiltersBase((prevFilters) => {
      const updatedFilters =
        typeof newFilters === 'function' ? newFilters(prevFilters) : newFilters;

      updateURL(searchQuery, updatedFilters, pagination.currentPage);
      return updatedFilters;
    });
  };

  // Update URL only when search is manually triggered (not on every keystroke)
  const updateURLForSearch = useCallback(
    (newQuery, newFilters, newPage = 1) => {
      updateURL(newQuery, newFilters, newPage);
    },
    [updateURL]
  );

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: newPage
    }));
    updateURL(searchQuery, filters, newPage);
    // Trigger new search with updated page
    handleSearch(searchQuery, filters, newPage);
  };

  // Phase 2 feature handlers

  // Similarity search handlers
  const handleFindSimilar = (caseData) => {
    if (!caseData?.caseNumber) return;

    // Store the source case for highlighting
    setSourceCase(caseData);

    // Clear current search and all filters, only keeping similarity filter
    setSearchQuery('');
    setSearchExecuted(false);

    const similarityFilters = {
      jurisdiction: '',
      courthouse: '',
      departments: [],
      caseStatus: '',
      judges: [],
      prosecutingAttorneys: [],
      defenseAttorneys: [],
      defendants: [],
      demographics: {
        race: null,
        minAge: null,
        maxAge: null,
        gender: null
      },
      charges: [],
      chargeLogic: 'OR',
      sentencingTypes: [],
      similarTo: caseData.caseNumber
    };

    setFiltersWithURL(similarityFilters);

    // Manually trigger search since we removed the auto-search useEffect
    handleSearch('', similarityFilters, 1, true, true);
  };

  // Recent searches handlers
  const handleSelectRecentSearch = (recentSearch) => {
    setSearchQuery(recentSearch.searchQuery);
    setFiltersBase(recentSearch.filters);
    // Manually trigger search since we removed the auto-search useEffect
    handleSearch(recentSearch.searchQuery, recentSearch.filters, 1, true, true);
  };

  const handleClearRecentSearches = () => {
    setRecentSearches([]);
  };

  // Case refresh handler
  const handleCaseUpdated = async (caseNumber) => {
    try {
      // Fetch the updated case data from the backend (this endpoint applies user edits)
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cases/${caseNumber}`
      );
      if (response.ok) {
        const caseData = await response.json();
        // Update the selected case if it's the same one
        if (selectedCase && selectedCase.caseNumber === caseNumber) {
          setSelectedCase(caseData);
        }

        // Update the case in search results if it exists
        setSearchResults((prevResults) =>
          prevResults.map((c) => (c.caseNumber === caseNumber ? caseData : c))
        );

        console.log('Case data refreshed successfully:', caseNumber);
      }
    } catch (error) {
      console.error('Error refreshing case data:', error);
    }
  };

  // Ref to prevent duplicate manual searches (React StrictMode protection)
  const lastManualSearchRef = useRef(0);

  const handleSearch = useCallback(
    async (
      searchQuery,
      filters,
      page = 1,
      updateURL = true,
      isManual = false
    ) => {
      // Check if user can perform search (free tier limits)
      if (!canPerformAction('searches')) {
        setSearchBlocked(true);
        return;
      }

      // Clear search blocked state if user can perform searches
      setSearchBlocked(false);

      // Prevent duplicate manual searches within 200ms (React StrictMode protection)
      if (isManual) {
        const now = Date.now();
        if (now - lastManualSearchRef.current < 200) {
          return;
        }
        lastManualSearchRef.current = now;
        lastSearchRef.current = now; // Update the global search timer
      }

      setIsLoading(true);

      try {
        const apiFilters = {
          ...filters,
          chargeCodes: filters.charges?.map((charge) => charge.code) || []
        };

        const requestBody = {
          searchQuery,
          filters: apiFilters,
          page: page,
          pageSize: 24, // Use fixed pageSize instead of pagination.pageSize
          sortBy: sortBy,
          sortOrder: sortOrder
        };

        const response = await fetch(API_URLS.SEARCH, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const data = await response.json();

        // Validate response structure
        if (!data.results || !Array.isArray(data.results)) {
          console.error('Invalid response format:', data);
          return;
        }

        // Update state only if we have valid data

        // Enhance results with similarity data if available
        const enhancedResults = data.similarityData
          ? data.results.map((docket) => {
              const similarityInfo = data.similarityData[docket.caseNumber];
              return similarityInfo
                ? {
                    ...docket,
                    _similarityScore: similarityInfo.similarityScore,
                    _matchReasons: similarityInfo.matchReasons
                  }
                : docket;
            })
          : data.results;

        // Search results set
        setSearchResults(enhancedResults);
        setPagination((prev) => ({
          ...prev,
          currentPage: page,
          totalResults: data.totalResults || data.results.length
        }));

        // Set execution time if available
        setExecutionTime(data.executionTime || null);

        // Mark search as executed
        setSearchExecuted(true);

        // Increment usage for manual searches (free tier tracking)
        if (isManual) {
          incrementUsage('searches');
        }

        // Update URL only for manual searches (not URL-triggered searches)
        if (updateURL) {
          isManualURLUpdateRef.current = true; // Flag to prevent URL params useEffect from running
          updateURLForSearch(searchQuery, filters, page);
        }

        // Save to recent searches (only for page 1, not pagination)
        if (page === 1) {
          const updatedRecent = saveRecentSearch(searchQuery, filters);
          setRecentSearches(updatedRecent);
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [updateURLForSearch, sortBy, sortOrder, canPerformAction, incrementUsage] // Include core dependencies
  );

  // Helper function to call search with specific sorting parameters
  const handleSearchWithSorting = useCallback(
    async (
      searchQuery,
      filters,
      page = 1,
      sortBy,
      sortOrder,
      updateURL = true,
      isManual = false
    ) => {
      // Prevent duplicate manual searches within 100ms (React StrictMode protection)
      if (isManual) {
        const now = Date.now();
        if (now - lastManualSearchRef.current < 100) {
          console.log('⏹️ Skipping duplicate manual search');
          return;
        }
        lastManualSearchRef.current = now;
      }

      setIsLoading(true);

      try {
        const apiFilters = {
          ...filters,
          chargeCodes: filters.charges?.map((charge) => charge.code) || []
        };

        const requestBody = {
          searchQuery,
          filters: apiFilters,
          page: page,
          pageSize: 24, // Use fixed pageSize instead of pagination.pageSize
          sortBy: sortBy,
          sortOrder: sortOrder
        };

        const response = await fetch(API_URLS.SEARCH, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const data = await response.json();

        // Validate response structure
        if (!data.results || !Array.isArray(data.results)) {
          console.error('Invalid response format:', data);
          return;
        }

        // Enhance results with similarity data if available
        const enhancedResults = data.similarityData
          ? data.results.map((docket) => {
              const similarityInfo = data.similarityData[docket.caseNumber];
              return similarityInfo
                ? {
                    ...docket,
                    _similarityScore: similarityInfo.similarityScore,
                    _matchReasons: similarityInfo.matchReasons
                  }
                : docket;
            })
          : data.results;

        // Search results set
        setSearchResults(enhancedResults);
        setPagination((prev) => ({
          ...prev,
          currentPage: page,
          totalResults: data.totalResults || data.results.length
        }));

        // Set execution time if available
        setExecutionTime(data.executionTime || null);

        // Mark search as executed
        setSearchExecuted(true);

        // Update URL only for manual searches (not URL-triggered searches)
        if (updateURL) {
          updateURLForSearch(searchQuery, filters, page);
        }

        // Save to recent searches (only for page 1, not pagination)
        if (page === 1) {
          const updatedRecent = saveRecentSearch(searchQuery, filters);
          setRecentSearches(updatedRecent);
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [updateURLForSearch]
  );

  // Handle sorting changes
  const handleSortChange = useCallback(
    (newSortBy, newSortOrder) => {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
      // Trigger a new search with the new sorting parameters
      // We need to call handleSearch with the new parameters directly
      handleSearchWithSorting(
        searchQuery,
        filters,
        1,
        newSortBy,
        newSortOrder,
        true,
        true
      );
    },
    [searchQuery, filters, handleSearchWithSorting]
  );

  // Auto-search when URL params change (e.g., on page reload) - Only run once on mount
  useEffect(() => {
    // Skip if this URL change was caused by a manual search
    if (isManualURLUpdateRef.current) {
      isManualURLUpdateRef.current = false;
      return;
    }

    const urlSearchQuery = searchParams.get('q');
    const urlFilters = searchParams.get('filters');
    const urlPage = searchParams.get('page');

    // Only auto-search if we have courts data loaded
    if (courts && courts.length > 0) {
      // Prevent duplicate calls within 200ms
      const now = Date.now();
      if (lastSearchRef.current && now - lastSearchRef.current < 200) {
        return;
      }
      lastSearchRef.current = now;

      if (urlSearchQuery || urlFilters) {
        // Search with URL parameters
        const filtersToUse = urlFilters
          ? JSON.parse(decodeURIComponent(urlFilters))
          : filters;
        const pageToUse = urlPage ? parseInt(urlPage) : 1;
        handleSearchRef.current(
          urlSearchQuery || '',
          filtersToUse,
          pageToUse,
          false
        ); // Don't update URL to prevent infinite loop
      } else {
        // No URL parameters - show initial unfiltered results
        handleSearchRef.current('', filters, 1, false, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courts, searchParams]);

  const handleSearchRef = useRef(handleSearch);
  const lastSearchRef = useRef(null);
  const isManualURLUpdateRef = useRef(false);

  // Keep handleSearch ref up to date
  useEffect(() => {
    handleSearchRef.current = handleSearch;
  }, [handleSearch]);

  // Auto-search useEffect removed - filter handlers in NewHeader already trigger searches
  // This prevents double searches when filters change

  // Dependency tracking removed for performance

  // Removed redundant similarity trigger useEffect - the main filters useEffect handles similarity search

  // Admin mode toggle
  if (isAdminMode) {
    // Show authentication form if not authenticated
    if (!isAuthenticated) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md w-full space-y-8">
            <div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                Admin Authentication
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                Enter admin password to access the dashboard
              </p>
            </div>
            <form
              className="mt-8 space-y-6"
              onSubmit={(e) => {
                e.preventDefault();
                const password = e.target.password.value;
                handleAdminLogin(password);
              }}
            >
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Admin password"
                />
              </div>

              {authError && (
                <div className="text-red-600 text-sm text-center">
                  {authError}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isAuthLoading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAuthLoading ? 'Authenticating...' : 'Sign in'}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setIsAdminMode(false)}
                  className="text-sm text-gray-600 hover:text-gray-500"
                >
                  Back to Search
                </button>
              </div>
            </form>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Admin Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <h1 className="text-2xl font-bold text-gray-900">
                  Briefed Admin
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleAdminLogout}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Logout
                </button>
                <button
                  onClick={() => setIsAdminMode(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Back to Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scraper Management Dashboard */}
        <ScraperManagementDashboard />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Admin Mode Toggle - Only show if admin is enabled */}
      {isAdminEnabled && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-2">
              <div className="text-sm text-blue-700">
                Admin access available
              </div>
              <button
                onClick={() => setIsAdminMode(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline"
              >
                Open Admin Dashboard →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Header with Search and Filters */}
      <NewHeader
        searchQuery={searchQuery}
        setSearchQuery={handleSearchQueryChange}
        onSearch={handleSearch}
        filters={filters}
        setFilters={setFiltersWithURL}
        courts={courts || []}
        recentSearches={recentSearches}
        onSelectRecentSearch={handleSelectRecentSearch}
        onClearRecentSearches={handleClearRecentSearches}
        searchExecuted={searchExecuted}
        setSearchExecuted={setSearchExecuted}
        onClearSourceCase={() => setSourceCase(null)}
      />

      {/* Usage Display for Free Tier */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-1 sm:py-2">
        <UsageDisplay />
      </div>

      {/* Email Verification Banner */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-1 sm:py-2">
        <EmailVerification />
      </div>

      {/* Search Blocked Message */}
      {searchBlocked && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Search Access Restricted
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {isInTrial ? (
                    <p>
                      Your free trial has ended. Upgrade to Pro to continue
                      searching and accessing case data.
                    </p>
                  ) : (
                    <p>
                      You need an active subscription to perform searches.
                      Please upgrade to Pro to continue.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-3 sm:py-8 overflow-x-hidden">
        {/* Mobile Layout - Stacked */}
        <div className="lg:hidden space-y-6">
          <EnhancedResultsSection
            results={searchResults}
            onCaseSelect={async (caseData) => {
              // Fetch the case fresh from the API to ensure edits are applied
              try {
                const response = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/cases/${caseData.caseNumber}`
                );
                if (response.ok) {
                  const caseWithEdits = await response.json();
                  setSelectedCase(caseWithEdits);
                } else {
                  // Fallback to search result data if API call fails
                  setSelectedCase(caseData);
                }
              } catch (error) {
                console.error('Error fetching case with edits:', error);
                // Fallback to search result data if API call fails
                setSelectedCase(caseData);
              }
            }}
            pagination={pagination}
            onPageChange={handlePageChange}
            isLoading={isLoading}
            searchQuery={searchQuery}
            filters={filters}
            onFindSimilar={handleFindSimilar}
            sortBy={sortBy}
            sortOrder={sortOrder}
            executionTime={executionTime}
            onSortChange={handleSortChange}
            sourceCase={sourceCase}
          />
        </div>

        {/* Desktop Layout - Two Column */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main content - 3 columns */}
            <div className="lg:col-span-3 space-y-6">
              <EnhancedResultsSection
                results={searchResults}
                onCaseSelect={async (caseData) => {
                  // Fetch the case fresh from the API to ensure edits are applied
                  try {
                    const response = await fetch(
                      `${process.env.REACT_APP_API_URL}/api/cases/${caseData.caseNumber}`
                    );
                    if (response.ok) {
                      const caseWithEdits = await response.json();
                      setSelectedCase(caseWithEdits);
                    } else {
                      // Fallback to search result data if API call fails
                      setSelectedCase(caseData);
                    }
                  } catch (error) {
                    console.error('Error fetching case with edits:', error);
                    // Fallback to search result data if API call fails
                    setSelectedCase(caseData);
                  }
                }}
                pagination={pagination}
                onPageChange={handlePageChange}
                isLoading={isLoading}
                searchQuery={searchQuery}
                filters={filters}
                onFindSimilar={handleFindSimilar}
                sortBy={sortBy}
                sortOrder={sortOrder}
                executionTime={executionTime}
                onSortChange={handleSortChange}
                sourceCase={sourceCase}
              />
            </div>

            {/* Judge & Prosecutor Analytics - Right Column */}
            <div className="lg:col-span-1">
              <div className="sticky top-6 h-[calc(100vh-120px)] overflow-y-auto">
                <JudgeProsecutorAnalytics
                  searchResults={searchResults}
                  filters={filters}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Search Insights Button - Mobile */}
      {searchResults && searchResults.length > 0 && (
        <div className="fixed bottom-3 right-3 sm:bottom-6 sm:right-6 z-50 lg:hidden">
          <button
            onClick={() => {
              console.log(
                'Insights button clicked, setting showSearchInsights to true'
              );
              console.log(
                'Current showSearchInsights state:',
                showSearchInsights
              );
              setShowSearchInsights(true);
            }}
            className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-3 bg-white text-gray-700 rounded-full shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <svg
              className="w-3.5 h-3.5 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <span className="text-xs sm:text-sm font-medium">Insights</span>
          </button>
        </div>
      )}

      {/* Mobile Search Insights Modal */}
      {showSearchInsights && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 lg:hidden modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowSearchInsights(false);
            }
          }}
          onTouchStart={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              setShowSearchInsights(false);
            }
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-hidden modal-content"
            data-modal-content
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => {
              e.stopPropagation();
              const modalContent = e.currentTarget;
              const isScrollingModal =
                modalContent.scrollHeight > modalContent.clientHeight;
              if (!isScrollingModal) {
                e.preventDefault();
              }
            }}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 px-2.5 sm:px-4 py-2 sm:py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 sm:gap-3">
                  <div className="p-1 sm:p-2 bg-gray-100 rounded-lg">
                    <svg
                      className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-lg font-semibold text-gray-900">
                      Search Insights
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500">
                      Judge and prosecutor analytics
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSearchInsights(false)}
                  className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Close
                </button>
              </div>
            </div>
            <div className="p-2 sm:p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Analytics panel is now integrated into the main results view */}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedCase && (
        <CaseDetailsModal
          caseData={selectedCase}
          onClose={() => setSelectedCase(null)}
          onFindSimilar={handleFindSimilar}
          onCaseUpdated={handleCaseUpdated}
        />
      )}

      {/* Legal Disclaimer Modal */}
      <LegalDisclaimerModal />
    </div>
  );
};

export default DocketGenie;
